// =============================================================================
// TIMELINE SECTION COMPONENT (V2 - NORMALIZED)
// =============================================================================

import React from 'react';
import { Calendar } from 'lucide-react';
import { layoutToClasses, displayToStyles } from '@/types/layout';
import type { ContentComponent } from '@/types';
import type { TimelineComponent } from '@/types/components';

interface TimelineSectionProps {
  component: ContentComponent;
}

/**
 * Type guard for new TimelineComponent format
 */
function isNewTimelineFormat(component: ContentComponent): boolean {
  return (
    component.type === 'timeline' &&
    'items' in component &&
    Array.isArray((component as any).items)
  );
}

/**
 * Timeline Section Component
 */
export const TimelineSection: React.FC<TimelineSectionProps> = ({ component }) => {
  if (isNewTimelineFormat(component)) {
    return <TimelineSectionNew component={component as unknown as any} />;
  }
  return <TimelineSectionLegacy component={component} />;
};

/**
 * New format Timeline Section
 */
const TimelineSectionNew: React.FC<{ component: TimelineComponent }> = ({ component }) => {
  const { id, content, items, layout, display } = component;
  
  const layoutClasses = layoutToClasses(layout);
  const displayStyles = displayToStyles(display);
  
  return (
    <section id={id} className={`content-section timeline-section ${layoutClasses}`} style={displayStyles}>
      <div className="container">
        {content.title && (
          <div className="section-header">
            <h2>{content.title}</h2>
            {content.description && <p>{content.description}</p>}
          </div>
        )}
        
        <div className="timeline-container">
          {items.map((item, index) => (
            <div key={item.id || index} className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}>
              <div className="timeline-marker">
                <div className="marker-dot"></div>
                {index !== items.length - 1 && <div className="marker-line"></div>}
              </div>
              
              <div className="timeline-content">
                <div className="timeline-date">
                  <Calendar size={16} />
                  <span>{item.date}</span>
                </div>
                
                <div className="timeline-card">
                  <div className="timeline-header">
                    {item.image && (
                      <div className="timeline-image">
                        <img src={item.image} alt={item.title} loading="lazy" />
                      </div>
                    )}
                    
                    <div className="timeline-title-section">
                      <h3>{item.title}</h3>
                      {item.icon && (
                        <div className="timeline-icon">
                          <i className={`icon-${item.icon}`}></i>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="timeline-description">
                    <p>{item.body}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Legacy format Timeline Section
 */
const TimelineSectionLegacy: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const timeline = metadata?.timeline || [];
  
  return (
    <section id={id} className="content-section timeline-section">
      <div className="container">
        {title && (
          <div className="section-header">
            <h2>{title}</h2>
            {content && <p>{content}</p>}
          </div>
        )}
        
        <div className="timeline-container">
          {timeline.map((item: any, index: number) => (
            <div key={index} className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}>
              <div className="timeline-marker">
                <div className="marker-dot"></div>
                {index !== timeline.length - 1 && <div className="marker-line"></div>}
              </div>
              
              <div className="timeline-content">
                <div className="timeline-date">
                  <Calendar size={16} />
                  <span>{item.date}</span>
                </div>
                
                <div className="timeline-card">
                  <div className="timeline-header">
                    {item.image && (
                      <div className="timeline-image">
                        <img src={item.image} alt={item.title} loading="lazy" />
                      </div>
                    )}
                    
                    <div className="timeline-title-section">
                      <h3>{item.title}</h3>
                      {item.icon && (
                        <div className="timeline-icon">
                          <i className={`icon-${item.icon}`}></i>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="timeline-description">
                    <p>{item.content}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export { TimelineSectionNew, TimelineSectionLegacy };

